import React, { ReactElement } from 'react';
import {
  AIReason,
  AppealReporterReason,
  Tag,
  TagsMetadata,
  UserReportItem
} from '../../../../state/types';
import styles from './TagInfo.module.scss';
import classNames from 'classnames';
import { AppealReasons } from '../../../../../../../../constants';
import { ReportReasons } from './ReportReasons';
import { aiReasonVerdictMap } from '../message-tags-constants';

const APPEAL_REASONS_TEXTS_MAP = {
  [AppealReasons.WasNotAwareGuidelines]: 'I was not aware of the community guidelines',
  [AppealReasons.DisagreeGuidelines]: 'I disagree with the community guidelines',
  [AppealReasons.DontUnderstandGuidelines]: 'I don’t understand the community guidelines',
  [AppealReasons.CommentFollowsGuidelines]: 'I believe my comment follows the community guidelines',
  [AppealReasons.Misunderstanding]:
    'I believe you misunderstood the intent or context of my comment',
  [AppealReasons.Other]: 'Other'
};

interface TagInfoProps {
  tag: Tag;
  metadata: TagsMetadata;
}

export const TagInfo = (props: TagInfoProps): ReactElement => {
  const { tag, metadata } = props;

  const renderInfo = () => {
    switch (tag) {
      case Tag.APPEAL: {
        const { appeal } = metadata;

        return (
          appeal && <CommenterAppeal reason={appeal.reason} description={appeal.other || ''} />
        );
      }
      case Tag.APPEAL_REPORTER: {
        const { report_appeals } = metadata;

        return report_appeals && <ReporterAppeal appeals={report_appeals.appeals} />;
      }
      case Tag.REPORTED:
        const { report_reasons_V2 } = metadata;
        return (
          report_reasons_V2 &&
          report_reasons_V2.reasons && <ReportReasons reasons={report_reasons_V2.reasons} />
        );

      case Tag.STAFF_REPORT:
        const { super_report_reasons_V2 } = metadata;
        return (
          super_report_reasons_V2 &&
          super_report_reasons_V2.reasons && (
            <ReportReasons reasons={super_report_reasons_V2.reasons} />
          )
        );
      case Tag.AI_MODERATED:
        const { ai_moderated } = metadata;
        return (
          ai_moderated &&
          ai_moderated.verdict !== 'approved' && <AIReasonComponent reason={ai_moderated} />
        );

      default:
        return null;
    }
  };

  const info = renderInfo();

  if (!info) {
    return null;
  }

  return (
    <div
      className={
        [Tag.REPORTED, Tag.STAFF_REPORT].includes(tag) ? styles.containerReport : styles.container
      }
    >
      {info}
    </div>
  );
};

export const ReasonItem = (props: UserReportItem): ReactElement => {
  const { username, description, className } = props;
  return (
    <div className={className}>
      <TagInfoUsername username={username} />
      <TagInfoDescription description={description} />
    </div>
  );
};

interface Appeal {
  reason: string;
  description: string;
}

const CommenterAppeal = (props: Appeal) => {
  const { reason, description } = props;

  return (
    <>
      <TagInfoReason
        reason={reason}
        className={description.length > 0 ? styles.withDescription : undefined}
      />
      <TagInfoDescription description={description} />
    </>
  );
};

const ReporterAppeal = (props: { appeals: Array<AppealReporterReason> }) => {
  const { appeals } = props;

  return (
    <div className={styles.reportAppeal}>
      {appeals.map(({ username, message }, index) => (
        <ReasonItem username={username} description={message} key={index} />
      ))}
    </div>
  );
};

const TagInfoUsername = (props: { username: string }) => {
  const { username } = props;

  return <div className={styles.username}>@{username}</div>;
};

const TagInfoReason = (props: { reason: string; className?: string }) => {
  const { reason, className } = props;

  return (
    <div className={classNames(styles.reason, className)}>
      {APPEAL_REASONS_TEXTS_MAP[reason] || reason}
    </div>
  );
};

const TagInfoDescription = (props: { description: string }) => {
  const { description } = props;

  return <div className={styles.description}>{description}</div>;
};

const AIReasonComponent = (props: { reason: AIReason }) => {
  const {
    reason: { verdict, evidence }
  } = props;

  const verdictLabel = aiReasonVerdictMap[verdict] || '';

  return (
    <>
      <TagInfoReason reason={verdictLabel} className={styles.withDescription} />
      <TagInfoDescription description={evidence} />
    </>
  );
};
